import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type {
  CategorySort,
  Store,
  StoreCategory,
  StoreComplaint,
  StoreFilter,
  StoreOrder,
  StorePriority,
  StoreSettlement,
  StoreSignUser,
  StoreVoucherUsage,
} from '@domain/models/store.model';

import type { ApiPagedResponse } from '@data/dto/api.dto';
import type {
  StoreCategoryDto,
  StoreComplaintDto,
  StoreDto,
  StoreOrderDto,
  StorePriorityDto,
  StoreSettlementDto,
  StoreSignUserDto,
  StoreVoucherUsageDto,
} from '@data/dto/store.dto';

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function asSort(value: number | string | undefined): CategorySort {
  const numeric = typeof value === 'string' ? Number(value) : (value ?? 2);
  if (numeric === 0 || numeric === 1 || numeric === 2) return numeric;
  return 2;
}

function emailText(value: string | string[] | null | undefined): string | null {
  if (!value) return null;
  return Array.isArray(value) ? value.filter(Boolean).join(', ') || null : value;
}

export function mapStoreDto(dto: StoreDto): Store {
  return {
    id: dto.storeId ?? 0,
    ownerUserId: dto.ownerUserId ?? 0,
    name: dto.name ?? '',
    description: dto.description ?? null,
    phone: dto.phone ?? null,
    logoImageUrl: dto.logoImageUrl ?? null,
    coverImageUrl: dto.coverImageUrl ?? null,
    videoUrl: dto.videoUrl ?? null,
    webSiteUrl: dto.webSiteUrl ?? null,
    mapUrl: dto.mapUrl ?? null,
    storeType: dto.storeType ?? null,
    storeStatus: dto.storeStatus ?? dto.storeStats ?? null,
    active: dto.active ?? false,
    isFavorite: dto.isFavorite ?? false,
    isCertified: dto.isCertified ?? false,
    isShowBuy: dto.isShowbuy ?? false,
    hasChanges: dto.hasChanges ?? false,
    rating: dto.rating ?? null,
    companyShare: dto.companyShare ?? 0,
    priorityId: dto.priorityId ?? 0,
    priorityName: dto.priorityName ?? '',
    locationCount: dto.locationCount ?? dto.storeLocations?.length ?? 0,
    merchantName: dto.userName ?? null,
    phoneNumber: dto.phoneNumber ?? dto.phone ?? null,
    note: dto.note ?? null,
    reasonActive: dto.reasonActive ?? null,
    signAction: dto.isAction ?? null,
    descriptionEntered: dto.descriptionEntered ?? null,
    onSuccessEntered: dto.onsuccessEntered ?? null,
    lastDateChange: dto.lastDateChange ?? null,
    createdOn: toDate(dto.createdOn),
    extraImageUrls: dto.extraImageUrls ?? [],
    locations: (dto.storeLocations ?? []).map((location) => ({
      id: location.storeLocationId ?? 0,
      name: location.name ?? '',
      latitude: location.latitude ?? 0,
      longitude: location.longitude ?? 0,
      address: location.address ?? null,
    })),
    vouchers: (dto.vouchers ?? []).map((voucher) => ({
      id: voucher.voucherId ?? 0,
      isOnline: voucher.isOnlineVoucher ?? false,
      code: voucher.code ?? null,
      explainCode: voucher.explainCode ?? null,
      value: voucher.value ?? 0,
      percent: voucher.percent ?? null,
      validityInDays: voucher.validityInDays ?? 0,
    })),
    categories: (dto.categories ?? []).map((category) => ({
      id: category.categoryId ?? 0,
      name: category.categoryName ?? '',
      nameArabic: category.categoryArName ?? null,
      logoUrl: category.categoryLogoUrl ?? null,
    })),
  };
}

export function mapPagedStores(response: ApiPagedResponse<StoreDto>): PagedResult<Store> {
  return {
    items: (response.items ?? []).map(mapStoreDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function applyStoreFilter(result: PagedResult<Store>, filter?: StoreFilter): PagedResult<Store> {
  if (!filter) return result;

  let items = [...result.items];

  if (filter.search?.trim()) {
    const term = filter.search.trim().toLowerCase();
    items = items.filter(
      (store) =>
        store.name.toLowerCase().includes(term) ||
        (store.merchantName?.toLowerCase().includes(term) ?? false) ||
        (store.phoneNumber?.includes(term) ?? false) ||
        String(store.id).includes(term),
    );
  }

  if (filter.status) {
    items = items.filter((store) => store.storeStatus === filter.status);
  }

  if (filter.storeType) {
    items = items.filter((store) => store.storeType === filter.storeType);
  }

  if (filter.active === true) {
    items = items.filter((store) => store.active);
  } else if (filter.active === false) {
    items = items.filter((store) => !store.active);
  }

  if (filter.categoryId) {
    items = items.filter((store) => store.categories.some((category) => category.id === filter.categoryId));
  }

  if (filter.hasChanges === true) {
    items = items.filter((store) => store.hasChanges);
  } else if (filter.hasChanges === false) {
    items = items.filter((store) => !store.hasChanges);
  }

  if (filter.createdFrom) {
    const from = new Date(filter.createdFrom);
    items = items.filter((store) => store.createdOn != null && store.createdOn >= from);
  }

  if (filter.createdTo) {
    const to = new Date(filter.createdTo);
    to.setHours(23, 59, 59, 999);
    items = items.filter((store) => store.createdOn != null && store.createdOn <= to);
  }

  const filtered =
    Boolean(filter.search) ||
    Boolean(filter.status) ||
    Boolean(filter.storeType) ||
    filter.active != null ||
    Boolean(filter.categoryId) ||
    filter.hasChanges != null ||
    Boolean(filter.createdFrom) ||
    Boolean(filter.createdTo);

  return {
    ...result,
    items,
    total: filtered ? items.length : result.total,
  };
}

export function mapStoreVoucherUsageDto(dto: StoreVoucherUsageDto): StoreVoucherUsage {
  return {
    totalVouchers: dto.totalVouchers ?? 0,
    totalVouchersUsed: dto.totalVouchersUsed ?? 0,
    unsettledVouchers: dto.unSettledVouchers ?? dto.unsettledVouchers ?? 0,
    vouchersUsedThisMonth: dto.vouchersUsedThisMonth ?? 0,
    vouchersUsedToday: dto.vouchersUsedToday ?? 0,
  };
}

export function mapStorePriorityDto(dto: StorePriorityDto): StorePriority {
  return {
    id: dto.priorityId ?? 0,
    name: dto.name ?? '',
    preference: dto.preference ?? 0,
  };
}

export function mapPagedPriorities(
  response: ApiPagedResponse<StorePriorityDto>,
): PagedResult<StorePriority> {
  return {
    items: (response.items ?? []).map(mapStorePriorityDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapStoreCategoryDto(dto: StoreCategoryDto): StoreCategory {
  return {
    id: dto.storeCategoryId ?? 0,
    name: dto.categoryName ?? '',
    nameArabic: dto.categoryNameArabic ?? '',
    logoImageUrl: dto.logoImageUrl ?? dto.LogoImageUrl ?? null,
    active: dto.active ?? false,
    sort: asSort(dto.storeSortCategory),
    createdOn: toDate(dto.createdOn),
  };
}

export function mapPagedStoreCategories(
  response: ApiPagedResponse<StoreCategoryDto>,
): PagedResult<StoreCategory> {
  return {
    items: (response.items ?? []).map(mapStoreCategoryDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapStoreComplaintDto(dto: StoreComplaintDto): StoreComplaint {
  return {
    id: dto.complaintId ?? 0,
    storeId: dto.storeId ?? 0,
    storeName: dto.storeName ?? '',
    complaint: dto.complaint ?? '',
    phoneNumber: dto.phoneNumber ?? null,
    createdOn: toDate(dto.createdOn),
  };
}

export function mapPagedComplaints(
  response: ApiPagedResponse<StoreComplaintDto>,
): PagedResult<StoreComplaint> {
  return {
    items: (response.items ?? []).map(mapStoreComplaintDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapStoreSettlementDto(dto: StoreSettlementDto): StoreSettlement {
  return {
    id: dto.orderSettlementId ?? 0,
    storeId: dto.storeId ?? 0,
    storeName: dto.storeName ?? '',
    telRReferenceId: dto.telRReferenceId ?? null,
    amount: dto.amount ?? 0,
    status: dto.status ?? '',
    settlementDate: toDate(dto.settlementDate),
  };
}

export function mapPagedSettlements(
  response: ApiPagedResponse<StoreSettlementDto>,
): PagedResult<StoreSettlement> {
  return {
    items: (response.items ?? []).map(mapStoreSettlementDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapStoreOrderDto(dto: StoreOrderDto): StoreOrder {
  return {
    storeName: dto.name ?? '',
    merchantName: dto.userName ?? null,
    phoneNumber: dto.phoneNumber ?? null,
    lines: (dto.order ?? []).map((line) => ({
      orderCount: line.orderCount ?? 0,
      code: line.code ?? null,
      userName: line.userName ?? null,
      explainCode: line.explainCode ?? null,
      isOnline: line.isOnlineVoucher ?? line.isOnlineVouche ?? false,
      createdOn: toDate(line.createdOn),
    })),
  };
}

export function mapPagedStoreOrders(response: ApiPagedResponse<StoreOrderDto>): PagedResult<StoreOrder> {
  return {
    items: (response.items ?? []).map(mapStoreOrderDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapStoreSignUserDto(dto: StoreSignUserDto): StoreSignUser {
  return {
    storeName: dto.storeName ?? '',
    userName: dto.userName ?? null,
    phoneNumber: dto.phoneNumber ?? null,
    email: emailText(dto.email),
  };
}

export function mapPagedSignUsers(
  response: ApiPagedResponse<StoreSignUserDto>,
): PagedResult<StoreSignUser> {
  return {
    items: (response.items ?? []).map(mapStoreSignUserDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function storeQueryParams(
  query: PageQuery,
  extra: Record<string, string | number | boolean> = {},
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: query.page,
    limit: query.pageSize,
    ...extra,
  };

  if (query.search) params['name'] = query.search;
  return params;
}
